import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { BlogList } from "@/components/site/BlogViews";
import { CtaBand, PageHero } from "@/components/site/Page";
import { InstagramFollowCta } from "@/components/site/Instagram";
import { listPublishedPosts } from "@/lib/blog.functions";
import { getInstagramFeed } from "@/lib/instagram.functions";
import { safeLoader } from "@/lib/safe-loader";

const TITLE = "Blog – Swedish International School in Spain";
const DESCRIPTION =
  "Notes from everyday life at the Swedish school in Estepona and Marbella: school life, teaching, sport and advice for families moving to the Costa del Sol.";

export const Route = createFileRoute("/en/blog/")({
  loader: async ({ context }) => {
    const [posts, instagram] = await Promise.all([
      safeLoader("en.blog.posts", () => context.queryClient.ensureQueryData({
        queryKey: ["blog-list", "en"],
        queryFn: () => listPublishedPosts({ data: { locale: "en", limit: 24 } }),
      }), []),
      safeLoader("en.blog.instagram", () => context.queryClient.ensureQueryData({
        queryKey: ["instagram-feed"],
        queryFn: () => getInstagramFeed(),
      }), { posts: [], highlights: [] }),
    ]);
    return { posts, instagramPosts: instagram.posts };
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/en/blog") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/blog"),
  }),
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      The blog could not be loaded right now. Please try again shortly.
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">Page not found.</div>
  ),
  component: BlogIndex,
});

function BlogIndex() {
  const { posts, instagramPosts } = Route.useLoaderData();
  return (
    <>
      <PageHero
        eyebrow="Blog"
        title="From everyday school life"
        intro="Here we write about teaching, school life and life on the Costa del Sol – the texts we would have wanted to read before our own move."
      />

      <BlogList posts={posts} locale="en" />

      <div className="py-10 sm:py-14">
        <InstagramFollowCta posts={instagramPosts} locale="en" />
      </div>

      <CtaBand locale="en" />
    </>
  );
}
